import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CairaAuthService } from './caira-auth.service';
import { RETURN_URL_PARAM, sanitizeReturnUrl } from './return-url.util';
import { LAB_REASON_CODES } from "./miles-ai-labs/api/ai-labs-refusal";

@Injectable()
export class CairaAuthInterceptor implements HttpInterceptor {
  // Resolve Router/CairaAuthService lazily (via Injector) to avoid any cyclic
  // dependency at bootstrap that injecting Router into an interceptor can cause.
  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Retrieve the Caira-specific token. Must match the key the QR login writes
    // (CairaAuthService.ACCESS_TOKEN = "caira_access_token").
    const token = localStorage.getItem('caira_access_token');
    const isCairaApi = req.url.includes('api.milescaira.com');

    /**
     * Only attach the token if:
     * 1. A token exists in localStorage
     * 2. The request URL belongs to the Caira API domain
     */
    const handled$ =
      token && isCairaApi
        ? next.handle(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
                skip: 'true',
              },
            })
          )
        : next.handle(req);

    // Only watch CAIRA API responses for auth expiry.
    if (!isCairaApi) return handled$;

    return handled$.pipe(
      catchError((err: HttpErrorResponse) => {
        if (this.isAuthExpiry(err)) {
          this.handleSessionExpiry();
        }
        return throwError(err);
      })
    );
  }

  /** A CAIRA 401 (token invalid/expired), or a 403 that clearly says the
   *  token/signature has expired, means the session is no longer valid. */
  private isAuthExpiry(err: HttpErrorResponse): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;
    if (err.status === 401) return true;
    if (err.status === 403) {
      // An AI Labs refusal is never a dead session. Checked BEFORE the regex
      // below, which is a substring test over the WHOLE serialised body —
      // including the `reason` key — so a labs message that happened to say
      // "expired" or "authentication" would log the learner out of the LMS
      // for what is really a permission or gating answer.
      //
      // None of today's labs messages trip it. This is the guard that keeps
      // that true when someone rewords one, because the failure is silent:
      // the learner lands on /login?sessionExpired=1 with no clue why.
      const reason = (err.error && err.error.reason) || '';
      if (typeof reason === 'string' && LAB_REASON_CODES.has(reason)) {
        return false;
      }
      // Redirect on token/session failures, but NOT on genuine permission 403s
      // (e.g. "You do not have permission to perform this action.").
      const detail = JSON.stringify(err.error ?? '').toLowerCase();
      return /expired|signature|invalid token|credentials|authentication|unauthor/.test(
        detail
      );
    }
    return false;
  }

  /** Clear the dead CAIRA session and send the user back to the QR login,
   *  instead of leaving a blank screen when API calls start failing. */
  private handleSessionExpiry(): void {
    const router = this.injector.get(Router);
    const cairaAuth = this.injector.get(CairaAuthService);
    const url = router.url || '';
    // Don't loop on the login-style screens (login / account-update / maintenance).
    if (/^\/(login|account-update|maintenance)/.test(url)) return;
    cairaAuth.logout();
    // Carry the page they were on, same as the auth guard does for a deep link,
    // so re-authenticating puts them back rather than on /home. sanitizeReturnUrl
    // drops the login-style screens, so the loop guard above is not weakened.
    const returnUrl = sanitizeReturnUrl(url);
    router.navigate(['/login'], {
      queryParams: {
        sessionExpired: 1,
        ...(returnUrl ? { [RETURN_URL_PARAM]: returnUrl } : {}),
      },
    });
  }
}
